import * as THREE from 'three';
import { MAP, PREMIUM_CITY } from '../utils/constants';
import { mulberry32 } from '../utils/rng';

export type PremiumCityAtmosphereHandle = {
  group: THREE.Group;
  update: (dt: number) => void;
};

type MovingDrone = {
  group: THREE.Group;
  radius: number;
  altitude: number;
  phase: number;
  speed: number;
};

function makeRing(radius: number, y: number, color: number, opacity: number): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(radius - 2.2, radius, 192), mat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = y;
  return ring;
}

function makeSearchlight(color: number): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.075,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const cone = new THREE.Mesh(
    new THREE.ConeGeometry(58, PREMIUM_CITY.SEARCHLIGHT_HEIGHT, 18, 1, true),
    mat,
  );
  cone.rotation.x = Math.PI;
  cone.position.y = PREMIUM_CITY.SEARCHLIGHT_HEIGHT * 0.5;
  return cone;
}

function makeDrone(rng: () => number): THREE.Group {
  const group = new THREE.Group();
  const color = rng() < 0.5 ? 0x62f7ff : 0xff5bd6;
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(3.2, 0.7, 1.5),
    new THREE.MeshBasicMaterial({ color: 0x07131e }),
  );
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(1.1, 8, 6),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.75,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }),
  );
  glow.position.y = 0.55;
  group.add(body, glow);
  return group;
}

/**
 * Premium decorative atmosphere: distant holo-rings, searchlight sweeps, rooftop
 * beacons, and tiny drone traffic. It never affects gameplay or collision.
 */
export function createPremiumCityAtmosphere(seed: number = 42): PremiumCityAtmosphereHandle {
  const group = new THREE.Group();
  group.name = 'PremiumCityAtmosphere';

  const rng = mulberry32(seed);
  const half = MAP.HALF;
  const searchlights: THREE.Mesh[] = [];
  const drones: MovingDrone[] = [];
  const beacons: THREE.Mesh[] = [];

  for (let i = 0; i < PREMIUM_CITY.SKYLINE_RING_COUNT; i++) {
    const r = half * (0.34 + i * 0.18);
    const ring = makeRing(r, 95 + i * 38, i % 2 === 0 ? 0x00eaff : 0xff3bd4, 0.045);
    ring.position.x = (i - 1) * 95;
    ring.position.z = half * (0.28 - i * 0.2);
    group.add(ring);
  }

  for (let i = 0; i < PREMIUM_CITY.SEARCHLIGHT_COUNT; i++) {
    const angle = (i / PREMIUM_CITY.SEARCHLIGHT_COUNT) * Math.PI * 2;
    const x = Math.sin(angle) * PREMIUM_CITY.SEARCHLIGHT_RADIUS;
    const z = Math.cos(angle) * PREMIUM_CITY.SEARCHLIGHT_RADIUS;
    const cone = makeSearchlight(i % 2 === 0 ? 0x6ee7ff : 0xff78d6);
    cone.position.x = x;
    cone.position.z = z;
    cone.rotation.z = 0.24 + rng() * 0.28;
    cone.rotation.y = -angle + Math.PI;
    searchlights.push(cone);
    group.add(cone);
  }

  const beaconGeo = new THREE.CylinderGeometry(1.2, 1.2, 9, 8);
  for (let i = 0; i < PREMIUM_CITY.BEACON_COUNT; i++) {
    const x = (rng() - 0.5) * half * 1.55;
    const z = (rng() - 0.5) * half * 1.55;
    const y = 42 + rng() * 95;
    const beacon = new THREE.Mesh(
      beaconGeo,
      new THREE.MeshBasicMaterial({
        color: rng() < 0.6 ? 0xff3658 : 0x4efcff,
        transparent: true,
        opacity: 0.34,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    beacon.position.set(x, y, z);
    beacons.push(beacon);
    group.add(beacon);
  }

  for (let i = 0; i < PREMIUM_CITY.DRONE_TRAFFIC_COUNT; i++) {
    const drone = makeDrone(rng);
    const radius = half * (0.18 + rng() * 0.56);
    const altitude =
      PREMIUM_CITY.DRONE_ALTITUDE_MIN +
      rng() * (PREMIUM_CITY.DRONE_ALTITUDE_MAX - PREMIUM_CITY.DRONE_ALTITUDE_MIN);
    const moving = {
      group: drone,
      radius,
      altitude,
      phase: rng() * Math.PI * 2,
      speed: 0.045 + rng() * 0.08,
    };
    drones.push(moving);
    group.add(drone);
  }

  let t = 0;
  const update = (dt: number) => {
    t += dt;
    for (let i = 0; i < searchlights.length; i++) {
      const light = searchlights[i]!;
      light.rotation.y += dt * (0.08 + i * 0.014);
      const mat = light.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.055 + Math.sin(t * 0.9 + i) * 0.018;
    }

    for (let i = 0; i < beacons.length; i++) {
      const mat = beacons[i]!.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.18 + 0.24 * Math.max(0, Math.sin(t * 2.4 + i * 0.61));
    }

    for (const drone of drones) {
      drone.phase += dt * drone.speed;
      const wobble = Math.sin(t * 1.4 + drone.phase * 3) * 8;
      drone.group.position.set(
        Math.sin(drone.phase) * drone.radius,
        drone.altitude + wobble,
        Math.cos(drone.phase * 0.87) * drone.radius,
      );
      drone.group.rotation.y = -drone.phase + Math.PI * 0.5;
    }
  };

  return { group, update };
}
